import type {
	IAdOrchestrator,
	IAdProvider,
	IAdSlotRegistry,
	ITargetingManager,
	SlotDefinitionParams,
} from '../types/ad-manager.interfaces';
import type { BannerType, IDefinedSlot } from '../types/google-tag.types';

export class AdOrchestrator implements IAdOrchestrator {
	constructor(
		private readonly provider: IAdProvider,
		private readonly registry: IAdSlotRegistry,
		private readonly targeting: ITargetingManager,
	) {}

	defineAndRegisterSlot(params: SlotDefinitionParams): void {
		const { bannerType, adUnitPath, defaultSize, containerId, sizeMappingEntries } = params;

		if (this.registry.hasSlot(containerId)) return;

		if (!this.provider.isReady()) return;

		let slot: IDefinedSlot | undefined = this.provider.defineSlot(adUnitPath, defaultSize, containerId);
		if (!slot) return;

		if (sizeMappingEntries.length > 0) {
			const mapping = this.provider.buildSizeMapping(sizeMappingEntries);
			if (mapping) {
				this.provider.addSizeMappingToSlot(slot, mapping);
			}
		}

		slot = this.provider.addServiceToSlot(slot);
		if (!slot) return;

		this.registry.register(bannerType, slot);
	}

	displaySlot(containerId: string): void {
		this.provider.cmdPush(() => this.provider.displayContainer(containerId));
	}

	refreshSlotsForType(type: BannerType, triggerContainerId: string): void {
		const slots = this.registry.getSlots(type);
		if (slots.length === 0) return;

		const lastSlot = slots[slots.length - 1];
		if (lastSlot.getSlotElementId() !== triggerContainerId) return;

		this.provider.refreshSlots(slots);
	}

	updateTargetingAndRefresh(type: BannerType, discipline: string | undefined): void {
		this.targeting.applyDisciplineTargeting(discipline);

		const slots = this.registry.getSlots(type);
		if (slots.length === 0) return;

		this.provider.refreshSlots(slots);
	}
}
